import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import api from '../services/ApiService';
import Dialer from './Dialer';
import HistoryPanel from './HistoryPanel';
import ContactsPanel from './ContactsPanel';
import DirectoryPanel from './DirectoryPanel';
import RecordingsPanel from './RecordingsPanel';
import MyStatePage from './MyStatePage';
import CrmPage from './CrmPage';
import AgentAIInbox from './AgentAIInbox';
import WhatsAppInbox from './WhatsAppInbox';
import QueueAIManagement from './QueueAIManagement';
import QueueAIPanel from './QueueAIPanel';
import { connectAgentQueueWS } from '../services/aiWebSocket';
import '../styles/admin.css';
import { can, isAgent, isPbxUser, isSupervisor, isContactCenterUser, getRoleLabel, getWorkspaceLabel } from '../utils/permissions';
import BLFPickupPanel from './BLFPickupPanel';
import AnimatedLogo from './AnimatedLogo';
import { useBranding } from '../context/BrandingContext';

interface DialerStatus {
  registration: string; liveConnected: boolean; agentState: string;
  callState: string; timer: number; muted: boolean; onHold: boolean;
  isRecording: boolean; remoteNumber: string;
}

function fmt(s: number) {
  const m = Math.floor(s / 60).toString().padStart(2,'0');
  return `${m}:${(s%60).toString().padStart(2,'0')}`;
}

const REG_COLOR: Record<string,string> = {
  registered:'#10b981','pbx-connected':'#10b981','ws-connected':'#f59e0b',
  'reg-failed':'#ef4444',disconnected:'#ef4444','no-sip-password':'#6b7280',connecting:'#6b7280',
};
const AGENT_COLOR: Record<string,string> = {
  ready:'#10b981', busy:'#ef4444', break:'#f59e0b', wrapup:'#8b5cf6', offline:'#6b7280',
};
const AGENT_LABEL: Record<string,string> = {
  ready:'Available', busy:'On Call', break:'On Break', wrapup:'Wrap-up', offline:'Offline',
};

// SVG nav icons
const Icons: Record<string, React.ReactElement> = {
  phone: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.69 19a19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 3.08 4.18 2 2 0 0 1 5.06 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L9.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  crm: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  aiq: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/><circle cx="12" cy="10" r="2" fill="currentColor" stroke="none"/><path d="M9 10h.01M15 10h.01" strokeWidth="2.5" strokeLinecap="round"/></svg>,
  aiinbox: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><circle cx="9" cy="10" r="1" fill="currentColor"/><circle cx="12" cy="10" r="1" fill="currentColor"/><circle cx="15" cy="10" r="1" fill="currentColor"/></svg>,
  contacts: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  history: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  recordings: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4" fill="currentColor" stroke="none"/></svg>,
  mystate: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
  myqueues: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z"/><path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>,
  settings: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93l-1.41 1.41M4.93 4.93l1.41 1.41M20 12h-2M6 12H4M19.07 19.07l-1.41-1.41M4.93 19.07l1.41-1.41M12 20v-2M12 6V4"/></svg>,
};

export default function AgentPortal({ user, sipPassword, sipUsername, dids = [], initialTab = '', onLogout }: any) {
  const activePageRef = useRef('phone');
  const [activePage, setActivePage] = useState(() => {
    if (initialTab === 'Dialer' || initialTab === 'phone') return 'phone';
    return 'phone'; // agents always start on phone
  });
  useEffect(() => { activePageRef.current = activePage; }, [activePage]);
  const [myQueues, setMyQueues] = useState<any[]>([]);
  const [aiWaitingCount, setAiWaitingCount] = useState(0);
  const [waRoleEnabled, setWaRoleEnabled]   = useState(false);
  const [waUnreadCount,  setWaUnreadCount]   = useState(0);
  const [blfCollapsed, setBlfCollapsed] = useState(false);
  const [showQueuePrompt, setShowQueuePrompt] = useState(false);

  useEffect(() => { api.getMyQueueMemberships().then(setMyQueues).catch(() => {}); }, []);

  useEffect(() => {
    axios.get('/api/ai/whatsapp-roles', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => {
        const s = r.data;
        const enabled =
          (user.role === 'agent'    && s.whatsapp_role_agent) ||
          (user.role === 'pbx_user' && s.whatsapp_role_pbx);
        setWaRoleEnabled(!!enabled);
      })
      .catch(() => {});
  }, [user.role]);

  const handleWaNewMessage = () => {
    if (activePageRef.current !== 'whatsapp') {
      setWaUnreadCount(c => c + 1);
    }
  };

  const fetchWaiting = useCallback(async () => {
    try {
      const r = await axios.get('/api/ai/conversations', {
        params: { status: 'WAITING_AGENT', page_size: 1 },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setAiWaitingCount(r.data.total || 0);
    } catch {}
  }, []);

  useEffect(() => {
    fetchWaiting();
    const poll = setInterval(fetchWaiting, 30_000);
    const token = localStorage.getItem('token');
    if (!token) return () => clearInterval(poll);
    const ws = connectAgentQueueWS(token);
    ws.on('escalation', () => { fetchWaiting(); setAiWaitingCount(c => c+1); });
    return () => { clearInterval(poll); ws.disconnect(); };
  }, [fetchWaiting]);

  const [status, setStatus] = useState<DialerStatus>({
    registration:'connecting', liveConnected:false, agentState:'ready',
    callState:'idle', timer:0, muted:false, onHold:false, isRecording:false, remoteNumber:'',
  });
  const changeAgentStateRef = useRef<((s:string)=>void)|null>(null);
  const [dialTo, setDialTo] = useState('');
  const [incomingCallFrom, setIncomingCallFrom] = useState<string|null>(null);

  // Theme: 'light' = Design D neumorphic, 'dark' = Design A premium dark
  const [theme, setTheme] = useState<'light'|'dark'>(() => (localStorage.getItem('ce-theme') as 'light'|'dark') || 'light');
  useEffect(() => {
    localStorage.setItem('ce-theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const handleDial = (n:string) => { setDialTo(n); setActivePage('phone'); };
  const handleDialUsed = () => setDialTo('');
  const handleStatusChange = (patch: any) => {
    if ('incomingFrom' in patch) setIncomingCallFrom(patch.incomingFrom ?? null);
    const { incomingFrom, ...rest } = patch;
    setStatus(prev => ({ ...prev, ...rest }));
  };
  const handleAgentStateChange = (s:string) => { if (changeAgentStateRef.current) changeAgentStateRef.current(s); };

  const branding  = useBranding();
  const hasBranding = !!(branding.logo || branding.primaryColor);

  useEffect(() => {
    if (!branding.loaded || hasBranding || !user?.tenant_id) return;
    branding.loadBranding(user.tenant_id);
  }, [branding.loaded]); // eslint-disable-line

  const shellCssVars = (() => {
    const p = hasBranding ? branding.primaryColor as string | null : null;
    if (!p) return {};
    const h = p.replace('#','');
    const lum = (parseInt(h.slice(0,2),16)*299 + parseInt(h.slice(2,4),16)*587 + parseInt(h.slice(4,6),16)*114) / 1000;
    if (lum >= 128) return { '--st-purple':p, '--st-purple-mid':p, '--st-purple-light':'#e9d5ff', '--st-active-bg':`${p}26`, '--st-active-text':'#f9fafb', '--st-sidebar-text':'#c4b5d4' } as React.CSSProperties;
    return {
      '--st-purple':'#C9A84C', '--st-purple-mid':'#A8872A', '--st-purple-light':'#E2BF6B',
      '--st-active-bg':'rgba(201,168,76,0.14)', '--st-active-text':'#E2BF6B',
      '--st-hover-bg':'rgba(201,168,76,0.08)', '--st-sidebar-text':'#94a3b8',
      '--st-bg-base':'#071428', '--st-bg-surface':'#0b1e3d',
      '--st-border':'rgba(201,168,76,0.2)', '--st-border-light':'rgba(201,168,76,0.12)',
    } as React.CSSProperties;
  })();

  const sidebarGold = (shellCssVars as any)['--st-purple'] === '#C9A84C';
  const accent      = sidebarGold ? '#C9A84C' : (branding.primaryColor || '#a855f7');
  const accentGlow  = sidebarGold ? 'rgba(201,168,76,0.12)' : 'rgba(168,85,247,0.12)';
  const accentBorder= sidebarGold ? 'rgba(201,168,76,0.3)'  : 'rgba(168,85,247,0.3)';
  const accentText  = sidebarGold ? '#E2BF6B' : '#e9d5ff';

  const isCCUser       = isContactCenterUser(user);
  const isAgentUser    = isAgent(user);
  const isSuperUser    = isSupervisor(user);
  const isPBXOnly      = isPbxUser(user) && !isCCUser;
  const agentColor = AGENT_COLOR[status.agentState] || '#6b7280';
  const isInCall   = ['calling','connected','ringing'].includes(status.callState);

  // Navigate to my-queues — PBX users who have no queues get a prompt
  const handleQueueClick = () => {
    if (!isCCUser && myQueues.length === 0) { setShowQueuePrompt(true); return; }
    setActivePage('my-queues');
  };

  const navItems = [
    // Always visible for every role
    { id:'phone',      label:'Phone',        icon:'phone',      show:true },
    // CC Agent + Supervisor only
    { id:'crm',        label:'CRM',          icon:'crm',        show:can(user,'crm_popup') },
    { id:'whatsapp',   label:'WhatsApp',     icon:'whatsapp',   show:waRoleEnabled, badge:waUnreadCount },
    { id:'ai-inbox',   label:'AI Inbox',     icon:'aiinbox',    show:can(user,'ai_transcription'), badge:aiWaitingCount },
    { id:'ai-queue',   label:'AI Queue',     icon:'aiq',        show:isSuperUser||user?.role==='tenant_admin'||user?.role==='platform_admin' },
    { id:'my-state',   label:'My State',     icon:'mystate',    show:isCCUser },
    { id:'my-queues',  label:'My Queues',    icon:'myqueues',   show:can(user,'queue_mode') },
    // Feature-flag or role-default gated (all roles)
    { id:'contacts',   label:'Contacts',     icon:'contacts',   show:can(user,'contacts') },
    { id:'directory',  label:'Directory',    icon:'contacts',   show:true },
    { id:'history',    label:'Recent Calls', icon:'history',    show:can(user,'recent_calls') },
    { id:'recordings', label:'Recordings',   icon:'recordings', show:can(user,'recording') },
    // Always visible
    { id:'settings',   label:'Settings',     icon:'settings',   show:true },
  ].filter(i => i.show !== false);

  const sbBg  = (shellCssVars as any)['--st-bg-base']    || '#071428';
  const topBg = (shellCssVars as any)['--st-bg-surface']  || '#0b1e3d';

  return (
    <div className="admin-shell" data-theme={theme} style={shellCssVars}>

      {/* ══════════════════════════ SIDEBAR ══════════════════════════ */}
      <div className="sidebar" style={{ background: sbBg, display:'flex', flexDirection:'column', width:220 }}>

        {/* Logo block */}
        <div style={{ padding:'20px 16px 14px', flexShrink:0 }}>
          {hasBranding && branding.logo ? (
            <div style={{
              background:'#fff', borderRadius:14, padding:'12px 10px 8px',
              boxShadow:'0 2px 12px rgba(0,0,0,.25)', marginBottom:10,
              display:'flex', flexDirection:'column', alignItems:'center',
            }}>
              <img src={branding.logo} alt={branding.companyName || ''}
                style={{ maxWidth:160, maxHeight:80, objectFit:'contain', display:'block' }} />
              {branding.companyName && (
                <div style={{ fontSize:9, fontWeight:700, color:'#0D2244', letterSpacing:'1.5px',
                  textTransform:'uppercase', marginTop:4, textAlign:'center' }}>
                  {branding.companyName}
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign:'center', marginBottom:10 }}>
              <AnimatedLogo width={140} style={{ display:'block', margin:'0 auto' }} />
              <div style={{ fontSize:13, fontWeight:800, color:'#f9fafb', marginTop:6 }}>CallEssential</div>
              <div style={{ fontSize:10, color:accent, fontWeight:500 }}>by Synergytalk</div>
            </div>
          )}

          {/* Workspace label */}
          <div style={{ textAlign:'center', fontSize:10, fontWeight:700, color:accent,
            textTransform:'uppercase', letterSpacing:'1.8px', marginBottom:10, opacity:.85 }}>
            {getWorkspaceLabel(user)}
          </div>

          {/* User identity pill */}
          <div style={{
            display:'flex', alignItems:'center', gap:10, padding:'8px 10px',
            borderRadius:10, background:accentGlow, border:`1px solid ${accentBorder}`,
          }}>
            <div style={{
              width:34, height:34, borderRadius:'50%', flexShrink:0,
              background:`linear-gradient(135deg,${accent}88,${accent})`,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontWeight:800, fontSize:14, color:'#1a0f00',
            }}>
              {(user.username||'U')[0].toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:'#f1f5f9' }}>{user.display_name || user.username}</div>
              {sipUsername && (
                <div style={{ fontSize:11, color:accent, fontWeight:600 }}>Ext {sipUsername}</div>
              )}
              <div style={{
                display:'inline-block', marginTop:3,
                fontSize:9.5, fontWeight:700, letterSpacing:'0.8px', textTransform:'uppercase',
                padding:'2px 7px', borderRadius:20,
                background: isSuperUser ? 'rgba(99,102,241,.18)' : isAgentUser ? 'rgba(16,185,129,.18)' : 'rgba(148,163,184,.18)',
                color: isSuperUser ? '#a5b4fc' : isAgentUser ? '#6ee7b7' : '#94a3b8',
              }}>
                {getRoleLabel(user)}
              </div>
              {dids && dids.length > 0 && (
                <div style={{ fontSize:10, color:'#94a3b8', marginTop:2, lineHeight:1.4 }}>
                  {(dids as string[]).map(d => <div key={d}>{d}</div>)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height:1, background:`${accent}22`, margin:'0 16px 8px', flexShrink:0 }} />

        {/* Nav items */}
        <div style={{ flex:1, overflowY:'auto', padding:'4px 8px' }}>
          {navItems.map(item => {
            const isActive = activePage === item.id;
            return (
              <div
                key={item.id}
                onClick={() => {
                  if (item.id === 'my-queues') { handleQueueClick(); return; }
                  if (item.id === 'ai-inbox') setAiWaitingCount(0);
                  if (item.id === 'whatsapp') setWaUnreadCount(0);
                  setActivePage(item.id);
                }}
                style={{
                  display:'flex', alignItems:'center', gap:11, padding:'10px 12px',
                  borderRadius:10, marginBottom:2, cursor:'pointer', position:'relative',
                  transition:'all .15s',
                  background: isActive ? accentGlow : 'transparent',
                  borderLeft: isActive ? `3px solid ${accent}` : '3px solid transparent',
                  color: isActive ? accentText : '#94a3b8',
                  fontWeight: isActive ? 700 : 500,
                  fontSize: 13.5,
                }}
                onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,.05)'; (e.currentTarget as HTMLDivElement).style.color = '#f1f5f9'; }}
                onMouseLeave={e => { if (!isActive) { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; (e.currentTarget as HTMLDivElement).style.color = '#94a3b8'; } }}
              >
                <span style={{ opacity: isActive ? 1 : .7, flexShrink:0, color: isActive ? accent : 'currentColor' }}>
                  {Icons[item.icon]}
                </span>
                <span style={{ flex:1 }}>{item.label}</span>
                {(item as any).badge > 0 && (
                  <span style={{
                    background:'#ef4444', color:'#fff', borderRadius:10,
                    padding:'1px 6px', fontSize:10, fontWeight:800, flexShrink:0,
                  }}>{(item as any).badge}</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Divider */}
        <div style={{ height:1, background:`${accent}22`, margin:'8px 16px 0', flexShrink:0 }} />

        {/* Bottom user card */}
        <div style={{
          margin:10, padding:'12px 12px', borderRadius:12,
          background:`linear-gradient(135deg,rgba(201,168,76,.08),rgba(27,86,219,.06))`,
          border:`1px solid ${accentBorder}`, flexShrink:0,
          display:'flex', alignItems:'center', gap:10,
        }}>
          <div style={{
            width:38, height:38, borderRadius:'50%', flexShrink:0,
            background:`linear-gradient(135deg,#b8963a,#C9A84C)`,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontWeight:800, fontSize:14, color:'#1a0f00',
          }}>
            {(user.username||'U')[0].toUpperCase()}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#f1f5f9',
              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {user.display_name || user.username}
            </div>
            {sipUsername && <div style={{ fontSize:11, color:accent, fontWeight:600 }}>Ext {sipUsername}</div>}
            <div style={{ fontSize:10, fontWeight:700, color: isSuperUser ? '#a5b4fc' : isAgentUser ? '#6ee7b7' : '#94a3b8',
              textTransform:'uppercase', letterSpacing:'0.6px', marginTop:1 }}>
              {getRoleLabel(user)}
            </div>
            {dids && dids.length > 0 && (
              <div style={{ fontSize:10, color:'#94a3b8', marginTop:1 }}>
                {(dids as string[]).join(' · ')}
              </div>
            )}
            <div style={{ display:'flex', alignItems:'center', gap:5, marginTop:2 }}>
              <span style={{ width:7, height:7, borderRadius:'50%', background:agentColor, display:'inline-block' }} />
              <span style={{ fontSize:11, color:'#94a3b8' }}>{AGENT_LABEL[status.agentState] || status.agentState}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════ MAIN AREA ══════════════════════════ */}
      <div className="main-area" style={{ display:'flex', flexDirection:'column', overflow:'hidden' }}>

        {/* Topbar */}
        <div className="topbar" style={{ background:topBg, borderBottom:`1px solid ${accentBorder}`, flexShrink:0 }}>
          <div className="topbar-title" style={{ fontSize:17, color:'#f1f5f9' }}>
            {navItems.find(i => i.id === activePage)?.label ?? 'Phone'}
          </div>
          <div className="topbar-user">
            {isInCall && (
              <span style={{
                display:'inline-flex', alignItems:'center', gap:7,
                background: status.callState === 'connected' ? '#fee2e2' : '#fef3c7',
                color: status.callState === 'connected' ? '#991b1b' : '#92400e',
                padding:'5px 12px', borderRadius:20, fontSize:12, fontWeight:700,
              }}>
                <span style={{ width:7,height:7,background:'currentColor',borderRadius:'50%',display:'inline-block',animation:'ce-pulse 1.2s ease-in-out infinite' }}/>
                {status.callState==='calling' ? `Calling ${status.remoteNumber}…` : status.callState==='connected' ? `In Call · ${fmt(status.timer)}` : 'Ringing'}
                {status.muted ? ' · Muted' : ''}{status.onHold ? ' · Hold' : ''}{status.isRecording ? ' · REC' : ''}
              </span>
            )}
            <div className="user-badge" style={{ background:`linear-gradient(135deg,#b8963a,#C9A84C)`, color:'#1a0f00' }}>
              {(user.username||'U')[0].toUpperCase()}
            </div>
            <button
              onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
              title={theme === 'dark' ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
              style={{
                width:32,height:32,borderRadius:'50%',border:'none',cursor:'pointer',
                background: theme === 'dark' ? 'rgba(255,255,255,.12)' : 'rgba(0,0,0,.07)',
                fontSize:15,display:'flex',alignItems:'center',justifyContent:'center',
                transition:'all .2s', flexShrink:0,
              }}
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <button className="danger-btn" onClick={onLogout} style={{ fontSize:12, padding:'7px 14px' }}>Logout</button>
          </div>
        </div>

        {/* Incoming call banner */}
        {incomingCallFrom && activePage !== 'phone' && (
          <div onClick={() => setActivePage('phone')} style={{
            background:`linear-gradient(135deg,#0D2244,#1A56DB)`, color:'white',
            padding:'10px 20px', cursor:'pointer',
            display:'flex', alignItems:'center', gap:12, flexShrink:0,
            animation:'ce-pulse 1.2s ease-in-out infinite',
          }}>
            <span style={{ fontSize:18 }}>📞</span>
            <span style={{ fontWeight:700, fontSize:13 }}>
              Incoming call from {incomingCallFrom} — click to answer
            </span>
          </div>
        )}

        {/* Page content */}
        <div style={{ flex:1, overflow:'hidden', display:'flex', flexDirection:'column', minHeight:0 }}>

          {/* Phone — always mounted */}
          <div style={{ display:activePage==='phone'?'flex':'none', flexDirection:'column', flex:1, minHeight:0, overflow:'hidden' }}>
            <Dialer
              user={user} sipPassword={sipPassword} sipUsername={sipUsername}
              onLogout={onLogout} embedded dialTo={dialTo}
              onDialUsed={handleDialUsed as any}
              changeAgentStateRef={changeAgentStateRef as any}
              onStatusChange={handleStatusChange as any}
              onOpenAIInbox={(() => setActivePage('ai-inbox')) as any}
              onOpenHistory={(() => setActivePage('history')) as any}
            />
          </div>

          {activePage === 'crm'        && <CrmPage onDial={handleDial} />}
          {activePage === 'whatsapp'   && <WhatsAppInbox user={user} onNewMessage={handleWaNewMessage} />}
          {activePage === 'ai-inbox'   && <AgentAIInbox user={user} />}
          {activePage === 'ai-queue'   && <QueueAIManagement user={user} />}
          {activePage === 'history'    && <HistoryPanel onDial={handleDial} />}
          {activePage === 'contacts'   && <ContactsPanel onDial={handleDial} />}
          {activePage === 'directory'  && <DirectoryPanel onDial={handleDial} />}
          {activePage === 'recordings' && <RecordingsPanel />}
          {activePage === 'my-state'   && <MyStatePage agentState={status.agentState} queueName="default" onStateChange={handleAgentStateChange} />}

          {activePage === 'my-queues' && (
            <div className="page-content" style={{ overflowY:'auto' }}>
              <div className="content-card">
                <div className="admin-page-title">My Queues</div>
                {myQueues.length === 0 ? (
                  <div style={{ textAlign:'center', padding:'32px 0' }}>
                    <div style={{ fontSize:40, marginBottom:12 }}>📭</div>
                    <div style={{ fontSize:16, fontWeight:700, color:'#0F172A', marginBottom:8 }}>
                      You are not assigned to any queues
                    </div>
                    <div style={{ fontSize:13, color:'#64748B' }}>
                      Contact your administrator to be added to a call queue.
                    </div>
                  </div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    {myQueues.map((q:any) => (
                      <div key={q.id} style={{
                        display:'flex', alignItems:'center', justifyContent:'space-between',
                        padding:'14px 16px', borderRadius:12, border:'1px solid #e5e7eb', background:'#f9fafb',
                      }}>
                        <div>
                          <div style={{ fontWeight:700, fontSize:15, color:'#0F172A' }}>{q.display_name || q.queue_name}</div>
                          <div style={{ fontSize:12, color:'#6b7280', marginTop:2 }}>Strategy: {q.strategy}</div>
                        </div>
                        <span style={{ background:'#dcfce7', color:'#166534', padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:700 }}>
                          Active Member
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activePage === 'settings' && (
            <div className="page-content" style={{ overflowY:'auto' }}>
              <div className="content-card">
                <div className="admin-page-title">Audio Settings</div>
                <p className="admin-muted">
                  Open the <strong>Phone</strong> tab and click <strong>⚙ Audio Settings</strong> to configure
                  microphone, speaker, and ringing device.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Group Call Pickup BLF Panel */}
      <BLFPickupPanel
        user={user}
        onDial={handleDial}
        isCollapsed={blfCollapsed}
        onToggle={() => setBlfCollapsed(b => !b)}
        themeAccent={accent}
        themeDark={theme === 'dark'}
      />

      {/* Queue prompt for non-CC users */}
      {showQueuePrompt && (
        <div style={{
          position:'fixed', inset:0, zIndex:9999,
          background:'rgba(7,20,40,.6)', backdropFilter:'blur(4px)',
          display:'flex', alignItems:'center', justifyContent:'center',
        }} onClick={() => setShowQueuePrompt(false)}>
          <div style={{
            background:'#fff', borderRadius:20, padding:'36px 40px',
            maxWidth:360, textAlign:'center',
            boxShadow:'0 24px 60px rgba(0,0,0,.25)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:48, marginBottom:12 }}>📭</div>
            <div style={{ fontSize:18, fontWeight:800, color:'#0F172A', marginBottom:8 }}>
              No Queue Assigned
            </div>
            <div style={{ fontSize:14, color:'#64748B', marginBottom:24 }}>
              You are not part of any call queue.<br/>
              Please contact your administrator to be assigned to a queue.
            </div>
            <button
              onClick={() => setShowQueuePrompt(false)}
              style={{
                padding:'10px 28px', borderRadius:10,
                background:'linear-gradient(135deg,#b8963a,#C9A84C)',
                border:'none', color:'#1a0f00', fontWeight:700, fontSize:14, cursor:'pointer',
              }}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
